package ai.wagemix.data.firebase

import ai.wagemix.data.WMAuthException
import ai.wagemix.net.models.WMAuthFailure
import dev.gitlive.firebase.auth.FirebaseAuthException
import dev.gitlive.firebase.auth.FirebaseAuthInvalidCredentialsException
import dev.gitlive.firebase.auth.FirebaseAuthInvalidUserException
import dev.gitlive.firebase.auth.FirebaseAuthUserCollisionException
import dev.gitlive.firebase.auth.FirebaseAuthWeakPasswordException
import kotlinx.coroutines.CancellationException

/** Maps the SDK's exceptions (and its `auth/...` error codes) to the typed reasons the UI renders. */
internal fun Throwable.toAuthFailure(): WMAuthFailure {
    val text = message.orEmpty()
    return when {
        this is FirebaseAuthWeakPasswordException -> WMAuthFailure.WeakPassword
        this is FirebaseAuthUserCollisionException -> WMAuthFailure.UserAlreadyExists
        this is FirebaseAuthInvalidUserException -> WMAuthFailure.AccountNotFound
        this is FirebaseAuthInvalidCredentialsException -> when {
            text.contains("invalid-email") -> WMAuthFailure.InvalidEmail
            text.contains("expired-action-code") -> WMAuthFailure.ExpiredLink
            text.contains("invalid-action-code") -> WMAuthFailure.InvalidLink
            else -> WMAuthFailure.InvalidCredentials
        }
        text.contains("auth/expired-action-code") -> WMAuthFailure.ExpiredLink
        text.contains("auth/invalid-action-code") -> WMAuthFailure.InvalidLink
        text.contains("auth/user-not-found") -> WMAuthFailure.AccountNotFound
        text.contains("auth/email-already-in-use") -> WMAuthFailure.UserAlreadyExists
        text.contains("auth/weak-password") -> WMAuthFailure.WeakPassword
        text.contains("auth/invalid-email") -> WMAuthFailure.InvalidEmail
        text.contains("auth/invalid-credential") || text.contains("auth/wrong-password") -> WMAuthFailure.InvalidCredentials
        text.contains("INVALID_LOGIN_CREDENTIALS") -> WMAuthFailure.InvalidCredentials
        text.contains("auth/too-many-requests") || text.contains("TOO_MANY_ATTEMPTS") -> WMAuthFailure.TooManyAttempts
        text.contains("auth/network-request-failed") -> WMAuthFailure.Network
        this is FirebaseAuthException -> WMAuthFailure.Unknown
        else -> WMAuthFailure.Unknown
    }
}

/**
 * Runs one identity-provider call, converting every SDK failure to a [WMAuthException] and
 * letting cancellation through (coroutines playbook, Step 4).
 */
internal suspend fun <T> authCall(block: suspend () -> T): T {
    try {
        return block()
    } catch (e: CancellationException) {
        throw e
    } catch (e: WMAuthException) {
        throw e
    } catch (e: Exception) {
        throw WMAuthException(e.toAuthFailure(), e.message)
    }
}
